import { useQuery } from "@tanstack/react-query";
import { useDynamicSupabase } from "@/hooks/useDynamicSupabase";

export interface PackageAnalytics {
  totalPackages: number;
  activePackages: number;
  draftPackages: number;
  expiredPackages: number;
  topPerformingPackages: Array<{
    id: string;
    title: string;
    bookings: number;
    revenue: number;
    conversionRate: number;
  }>;
  averagePackageRating: number;
  totalPackageViews: number;
  totalRevenue: number;
  averagePrice: number;
}

export const usePackageAnalytics = () => {
  const { supabase } = useDynamicSupabase();
  
  return useQuery({
    queryKey: ["package-analytics"],
    queryFn: async (): Promise<PackageAnalytics> => {
      // Get all tourism packages
      const { data: packages, error: packagesError } = await supabase
        .from("tourism_packages")
        .select("*");

      if (packagesError) throw packagesError;

      // Get bookings data
      const { data: bookings, error: bookingsError } = await supabase
        .from("package_bookings_enhanced")
        .select("*");

      // Get KPI metrics
      const { data: kpis, error: kpisError } = await supabase
        .from("package_kpi_metrics")
        .select("*") as any;

      const totalPackages = packages?.length || 0;
      const activePackages = packages?.filter(p => p.status === 'active').length || 0;
      const draftPackages = packages?.filter(p => p.status === 'draft').length || 0;
      const expiredPackages = packages?.filter(p => p.status === 'expired').length || 0;

      // Calculate top performing packages
      const packagePerformance = packages?.map(pkg => {
        const packageBookings = bookings?.filter((b: any) => 
          b.package_id === pkg.id && 
          (b.booking_status === 'confirmed' || b.booking_status === 'completed')
        ) || [];
        
        const packageViews = kpis?.filter((k: any) => k.package_id === pkg.id)
          .reduce((sum: number, k: any) => sum + (k.views_count || 0), 0) || 0;
        
        const revenue = packageBookings.reduce((sum: number, b: any) => sum + (b.total_amount || 0), 0);
        const conversionRate = packageViews > 0 
          ? (packageBookings.length / packageViews) * 100 
          : 0;

        return {
          id: pkg.id,
          title: pkg.title,
          bookings: packageBookings.length,
          revenue,
          conversionRate: Number(conversionRate.toFixed(2))
        };
      }) || [];

      const topPerformingPackages = packagePerformance
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      const totalViews = kpis?.reduce((sum: number, k: any) => sum + (k.views_count || 0), 0) || 0;
      const totalRevenue = bookings?.reduce((sum: number, b: any) => 
        (b.booking_status === 'confirmed' || b.booking_status === 'completed') 
          ? sum + (b.total_amount || 0) 
          : sum, 0) || 0;
      
      const averageRating = kpis?.reduce((sum: number, k: any) => sum + (k.customer_satisfaction_score || 0), 0) / (kpis?.length || 1) || 0;
      const averagePrice = packages?.reduce((sum, p) => sum + (p.price || 0), 0) / (packages?.length || 1) || 0;

      return {
        totalPackages,
        activePackages,
        draftPackages,
        expiredPackages,
        topPerformingPackages,
        averagePackageRating: Number(averageRating.toFixed(2)),
        totalPackageViews: totalViews,
        totalRevenue,
        averagePrice
      };
    }
  });
};
